/**
 * PortfolioArchetypePage — /portfolio-kit/archetype/:slug
 *
 * One page, three archetypes (RAG Agent / Multi-Agent Researcher / HITL Workflow).
 * Deep-dive content. Tier gating on all-access archetypes (RAG is free-preview).
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getArchetypeBySlug, PROJECT_ARCHETYPES } from "../data/portfolioKit";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// severityColor resolved at render time via theme — see usage in JSX
// low → text.secondary, medium → warning.main, high → warning.dark (AMBIGUOUS: orange), critical → error.main

export default function PortfolioArchetypePage() {
  const { t, i18n } = useTranslation("pages-products");
  const theme = useTheme();
  const { slug } = useParams<{ slug: string }>();

  // severity → token (resolved here so theme is in scope)
  const severityColor: Record<string, string> = {
    low: theme.palette.text.secondary,
    medium: theme.palette.warning.main,
    high: theme.palette.warning.dark,   // orange — AMBIGUOUS: warning.dark
    critical: theme.palette.error.main,
  };
  const navigate = useNavigate();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const archetype = slug ? getArchetypeBySlug(slug, i18n.language) : undefined;

  // Tier gating — all_access archetypes require login + All Access tier.
  // We show a paywall upsell card if gated (not a hard block — users should see what's inside).
  const tierAccess = getTierAccess(user);
  const isAllAccess = tierAccess.canAccessProducts || tierAccess.isModerator;
  const contentGated = archetype?.tier === "all_access" && !isAllAccess;

  if (!archetype) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4">{t("PortfolioArchetypePage.notFoundTitle")}</Typography>
        <Button onClick={() => navigate("/portfolio-kit")} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          {t("PortfolioArchetypePage.backToPortfolioKitButton")}
        </Button>
      </Container>
    );
  }

  const currentIndex = PROJECT_ARCHETYPES.findIndex((a) => a.en.slug === archetype.slug);
  const nextArchetypeWrapped =
    currentIndex >= 0 && currentIndex < PROJECT_ARCHETYPES.length - 1
      ? PROJECT_ARCHETYPES[currentIndex + 1]
      : null;
  const nextArchetype = nextArchetypeWrapped ? L(nextArchetypeWrapped, i18n.language) : null;

  const handleCopyDiagram = () => {
    navigator.clipboard.writeText(archetype.architecture.mermaidDiagram);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/portfolio-kit")}
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {t("PortfolioArchetypePage.backToPortfolioKitButton")}
          </Button>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={0.5}>
            <Chip
              label={archetype.tier === "free" ? t("PortfolioArchetypePage.freePreviewChipLabel") : t("PortfolioArchetypePage.allAccessChipLabel")}
              icon={archetype.tier === "all_access" ? <LockIcon sx={{ fontSize: 14 }} /> : undefined}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.65rem",
                bgcolor: archetype.tier === "free" ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.primary.main, 0.12),
                color: archetype.tier === "free" ? theme.palette.success.dark : theme.palette.primary.main,
              }}
            />
            <Chip
              label={archetype.difficulty.toUpperCase()}
              size="small"
              sx={{ fontWeight: 500, fontSize: "0.65rem", bgcolor: alpha(theme.palette.text.secondary, 0.1) }}
            />
            <Chip
              label={archetype.callbackSignal === "very_high" ? t("PortfolioArchetypePage.veryHighCallbackSignalChipLabel") : t("PortfolioArchetypePage.highCallbackSignalChipLabel")}
              size="small"
              sx={{ fontWeight: 500, fontSize: "0.65rem", bgcolor: alpha(theme.palette.warning.dark, 0.1), color: theme.palette.warning.dark }}
            />
            <Chip
              label={t("PortfolioArchetypePage.buildTimeChipLabel", { time: archetype.estimatedBuildTime })}
              size="small"
              sx={{ fontWeight: 500, fontSize: "0.65rem", bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}
            />
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1.5 }}>
            {archetype.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 820, lineHeight: 1.5 }}>
            {archetype.tagline}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={5}>
          {/* Problem */}
          <Section title={t("PortfolioArchetypePage.sectionTitleProblem")}>
            <Typography variant="h6" fontWeight={500} mb={1}>
              {archetype.problem.headline}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={2} sx={{ lineHeight: 1.7 }}>
              {archetype.problem.detail}
            </Typography>
            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
              <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}>
                {t("PortfolioArchetypePage.whyItMattersIn2026Label")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                {archetype.problem.whyItMatters}
              </Typography>
            </Paper>
          </Section>

          {/* Architecture */}
          <Section title={t("PortfolioArchetypePage.sectionTitleArchitecture")}>
            <Typography variant="body1" color="text.secondary" mb={2} sx={{ lineHeight: 1.7 }}>
              {archetype.architecture.summary}
            </Typography>
            <Paper
              sx={{
                p: 0,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.05),
                  borderBottom: `1px solid ${alpha(theme.palette.background.paper, 0.1)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: alpha(theme.palette.background.paper, 0.6), fontFamily: "monospace" }}>
                  {t("PortfolioArchetypePage.mermaidFileCaption")}
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={handleCopyDiagram}
                  sx={{ color: alpha(theme.palette.background.paper, 0.8), textTransform: "none", fontSize: "0.75rem" }}
                >
                  {copied ? t("PortfolioArchetypePage.copiedLabel") : t("PortfolioArchetypePage.copyLabel")}
                </Button>
              </Stack>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  color: theme.palette.success.light,
                  fontSize: "0.82rem",
                  fontFamily: "'JetBrains Mono', Menlo, monospace",
                  overflow: "auto",
                  lineHeight: 1.5,
                }}
              >
                {archetype.architecture.mermaidDiagram}
              </Box>
            </Paper>
            <Box mt={3}>
              <Typography variant="body2" fontWeight={500} mb={1} sx={{ color: theme.palette.primary.main }}>
                {t("PortfolioArchetypePage.keyComponentsLabel")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {archetype.architecture.components.map((c) => (
                  <Chip
                    key={c.id}
                    label={`${c.label} · ${c.role}`}
                    size="small"
                    sx={{ fontSize: "0.72rem", bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }}
                  />
                ))}
              </Stack>
            </Box>
          </Section>

          {/* Tier gate divider for all_access content */}
          {contentGated && (
            tierAccess.isPro ? <ProUpsellNote /> : (
              <Paper
                sx={{
                  p: 4,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h5" fontWeight={500} mb={1}>
                  {t("PortfolioArchetypePage.gateTitle")}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={2} sx={{ maxWidth: 600, mx: "auto" }}>
                  {t("PortfolioArchetypePage.gateDescription")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                  <Button
                    variant="contained"
                    onClick={() => navigate("/pricing")}
                    sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
                  >
                    {t("PortfolioArchetypePage.unlockAllAccessButton")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/portfolio-kit/archetype/${PROJECT_ARCHETYPES[0].en.slug}`)}
                    sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
                  >
                    {t("PortfolioArchetypePage.seeFreePreviewButton")}
                  </Button>
                </Stack>
              </Paper>
            )
          )}

          {/* Remaining sections — only rendered if ungated */}
          {!contentGated && (
            <>
              {/* Tech Stack */}
              <Section title={t("PortfolioArchetypePage.sectionTitleTechStack")}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} mb={1} sx={{ color: theme.palette.success.main }}>
                      {t("PortfolioArchetypePage.requiredLabel")}
                    </Typography>
                    <Stack spacing={0.5}>
                      {archetype.techStack.required.map((item) => (
                        <Typography key={item} variant="body2" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.success.main, 0.4)}` }}>
                          {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} mb={1} sx={{ color: theme.palette.text.secondary }}>
                      {t("PortfolioArchetypePage.optionalLabel")}
                    </Typography>
                    <Stack spacing={0.5}>
                      {archetype.techStack.optional.map((item) => (
                        <Typography key={item} variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.text.secondary, 0.3)}` }}>
                          {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} mb={1} sx={{ color: theme.palette.error.main }}>
                      {t("PortfolioArchetypePage.avoidAsStartingPointLabel")}
                    </Typography>
                    <Stack spacing={0.5}>
                      {archetype.techStack.avoidIfNovice.map((item) => (
                        <Typography key={item} variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
                          {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Section>

              {/* Build Phases */}
              <Section title={t("PortfolioArchetypePage.sectionTitleBuildPhases")}>
                <Stack spacing={2}>
                  {archetype.buildPhases.map((p, idx) => (
                    <Paper
                      key={p.phase}
                      sx={{ p: 2.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, borderLeft: `4px solid ${theme.palette.primary.main}` }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {p.phase}
                        </Typography>
                        <Chip label={p.durationEstimate} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main, fontWeight: 500 }} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {p.goal}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Section>

              {/* Eval Harness */}
              <Section title={t("PortfolioArchetypePage.sectionTitleEvaluationHarness")}>
                <Typography variant="body1" color="text.secondary" mb={2} sx={{ lineHeight: 1.7 }}>
                  {archetype.evalHarness.summary}
                </Typography>
                <Paper sx={{ overflow: "hidden", border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1.5fr 1.5fr 1fr" },
                      gap: 0,
                    }}
                  >
                    <CellHeader>{t("PortfolioArchetypePage.metricColumnHeader")}</CellHeader>
                    <CellHeader>{t("PortfolioArchetypePage.whatItMeasuresColumnHeader")}</CellHeader>
                    <CellHeader>{t("PortfolioArchetypePage.howMeasuredColumnHeader")}</CellHeader>
                    <CellHeader>{t("PortfolioArchetypePage.targetColumnHeader")}</CellHeader>
                    {archetype.evalHarness.metrics.map((m, idx) => (
                      <Box key={m.name} sx={{ display: "contents" }}>
                        <Cell bold first>{m.name}</Cell>
                        <Cell>{m.what}</Cell>
                        <Cell>{m.howMeasured}</Cell>
                        <Cell mono>{m.targetRange}</Cell>
                      </Box>
                    ))}
                  </Box>
                </Paper>
                <Paper sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                  <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}>
                    {t("PortfolioArchetypePage.datasetGuidanceLabel")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                    {archetype.evalHarness.datasetGuidance}
                  </Typography>
                </Paper>
              </Section>

              {/* HITL — agent archetypes only */}
              {archetype.hitlPattern && (
                <Section title={t("PortfolioArchetypePage.sectionTitleHitl")}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <Paper sx={{ p: 2.5, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ color: theme.palette.error.main, mb: 1 }}>
                        {t("PortfolioArchetypePage.triggersLabel")}
                      </Typography>
                      <Stack spacing={0.8}>
                        {archetype.hitlPattern.triggers.map((trigger) => (
                          <Typography key={trigger} variant="body2" sx={{ lineHeight: 1.5 }}>
                            • {trigger}
                          </Typography>
                        ))}
                      </Stack>
                    </Paper>
                    <Paper sx={{ p: 2.5, border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ color: theme.palette.warning.main, mb: 1 }}>
                        {t("PortfolioArchetypePage.humanDecisionsLabel")}
                      </Typography>
                      <Stack spacing={0.8}>
                        {archetype.hitlPattern.humanDecisions.map((d) => (
                          <Typography key={d} variant="body2" sx={{ lineHeight: 1.5 }}>
                            • {d}
                          </Typography>
                        ))}
                      </Stack>
                    </Paper>
                  </Box>
                  <Paper sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.04), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                    <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.success.main, letterSpacing: "0.08em" }}>
                      {t("PortfolioArchetypePage.autoResumeLearningLoopLabel")}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                      {archetype.hitlPattern.autoResumeRules}
                    </Typography>
                  </Paper>
                </Section>
              )}

              {/* Failure Modes */}
              <Section title={t("PortfolioArchetypePage.sectionTitleFailureModes")}>
                <Stack spacing={1.5}>
                  {archetype.failureModes.map((f) => (
                    <Paper
                      key={f.name}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${alpha(severityColor[f.severity], 0.3)}`,
                        borderLeft: `4px solid ${severityColor[f.severity]}`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WarningAmberIcon sx={{ color: severityColor[f.severity], fontSize: 20 }} />
                          <Typography variant="body1" fontWeight={500}>
                            {f.name}
                          </Typography>
                        </Stack>
                        <Chip
                          label={f.severity.toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: alpha(severityColor[f.severity], 0.1),
                            color: severityColor[f.severity],
                            fontWeight: 500,
                            fontSize: "0.65rem",
                          }}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <Trans
                          i18nKey="PortfolioArchetypePage.triggerLabel"
                          t={t}
                          values={{ trigger: f.trigger }}
                          components={[<strong key="strong0" />]}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <Trans
                          i18nKey="PortfolioArchetypePage.mitigationLabel"
                          t={t}
                          values={{ mitigation: f.mitigation }}
                          components={[<strong key="strong0" />]}
                        />
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Section>

              {/* Deployment */}
              <Section title={t("PortfolioArchetypePage.sectionTitleDeployment")}>
                <Typography variant="body2" mb={2} sx={{ lineHeight: 1.7 }}>
                  <Trans
                    i18nKey="PortfolioArchetypePage.minimumViableDeploymentLabel"
                    t={t}
                    values={{ deployment: archetype.deployment.minimumViable }}
                    components={[<strong key="strong0" />]}
                  />
                </Typography>
                <Typography variant="body2" fontWeight={500} mb={1} sx={{ color: theme.palette.primary.main }}>
                  {t("PortfolioArchetypePage.observabilityMinimumLabel")}
                </Typography>
                <Stack spacing={0.5} mb={2}>
                  {archetype.deployment.observabilityMinimum.map((o) => (
                    <Typography key={o} variant="body2" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.4)}` }}>
                      {o}
                    </Typography>
                  ))}
                </Stack>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                  <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.warning.main, letterSpacing: "0.08em" }}>
                    {t("PortfolioArchetypePage.costCeilingLabel")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                    {archetype.deployment.costCeiling}
                  </Typography>
                </Paper>
              </Section>

              {/* Domain Evidence */}
              <Section title={t("PortfolioArchetypePage.sectionTitleDomainEvidence")}>
                <Typography variant="body2" color="text.secondary" mb={2} sx={{ lineHeight: 1.7 }}>
                  {t("PortfolioArchetypePage.domainEvidenceIntro")}
                </Typography>
                <Stack spacing={1.5}>
                  {archetype.domainEvidence.map((d) => (
                    <Paper
                      key={d.domain}
                      sx={{
                        p: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "100px 1fr" },
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Chip
                          label={d.domain}
                          sx={{
                            fontWeight: 500,
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.background.paper,
                            fontSize: "0.85rem",
                            height: 32,
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                          {d.domainName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {d.howThisProjectShows}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Section>

              {/* Portfolio Framing */}
              <Section title={t("PortfolioArchetypePage.sectionTitlePortfolioFraming")}>
                <Paper sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}>
                    {t("PortfolioArchetypePage.portfolioCardOneLineLabel")}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 1, mb: 2, lineHeight: 1.6, fontFamily: "'Fraunces', Georgia, serif" }}>
                    "{archetype.portfolioFraming.oneLineDescription}"
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}>
                    {t("PortfolioArchetypePage.metricsToHighlightLabel")}
                  </Typography>
                  <Stack spacing={0.5} mt={1} mb={2}>
                    {archetype.portfolioFraming.metricsToHighlight.map((m) => (
                      <Typography key={m} variant="body2">
                        • {m}
                      </Typography>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.error.main, letterSpacing: "0.08em" }}>
                    {t("PortfolioArchetypePage.antiPatternLabel")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1} sx={{ lineHeight: 1.6 }}>
                    {archetype.portfolioFraming.antiPattern}
                  </Typography>
                </Paper>
              </Section>

              {/* Repo Structure */}
              <Section title={t("PortfolioArchetypePage.sectionTitleRepoStructure")}>
                <Paper sx={{ p: 0, bgcolor: theme.palette.background.paper, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, borderRadius: 2, overflow: "hidden" }}>
                  <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.background.paper, 0.05), borderBottom: `1px solid ${alpha(theme.palette.background.paper, 0.1)}` }}>
                    <Typography variant="caption" sx={{ color: alpha(theme.palette.background.paper, 0.6), fontFamily: "monospace" }}>
                      {t("PortfolioArchetypePage.repoRootLabel")}
                    </Typography>
                  </Box>
                  <Box component="pre" sx={{ m: 0, p: 2, color: theme.palette.success.light, fontSize: "0.82rem", fontFamily: "'JetBrains Mono', Menlo, monospace", overflow: "auto", lineHeight: 1.6 }}>
{archetype.repoStructure.map((r) => `  ${r}`).join("\n")}
                  </Box>
                </Paper>
              </Section>
            </>
          )}

          {/* Footer nav */}
          <Divider sx={{ my: 3 }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/portfolio-kit")}
              sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
            >
              {t("PortfolioArchetypePage.allArchetypesButton")}
            </Button>
            {nextArchetype && (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/portfolio-kit/archetype/${nextArchetype.slug}`)}
                sx={{ bgcolor: theme.palette.primary.main, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
              >
                {t("PortfolioArchetypePage.nextArchetypeButton", { title: nextArchetype.title.split("—")[0].trim() })}
              </Button>
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="h5" fontWeight={500} mb={2} sx={{ letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function CellHeader({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 2,
        py: 1.2,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: theme.palette.primary.main,
      }}
    >
      {children}
    </Box>
  );
}

function Cell({
  children,
  bold,
  mono,
  first,
}: {
  children: React.ReactNode;
  bold?: boolean;
  mono?: boolean;
  first?: boolean;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 2,
        py: 1.2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: "0.85rem",
        fontWeight: bold ? 500 : 400,
        fontFamily: mono ? "'JetBrains Mono', Menlo, monospace" : "inherit",
        color: bold ? "text.primary" : "text.secondary",
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  );
}
